import { useEffect } from "react";
import { Bell, Search } from "lucide-react";
import { useT } from "../../lib/i18n";
import { cx } from "../../lib/cx";
import { Button } from "./Button";
import { dsFont } from "./styles";

type Props = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  onNotify?: () => void;
  primaryLabel: string;
  onPrimary?: () => void;
};

/** Search (`/`), notifications and the primary action, aligned to the right. */
export function TopBar({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  onNotify,
  primaryLabel,
  onPrimary,
}: Props) {
  const t = useT();

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      event.preventDefault();
      document.getElementById("ds-topbar-search")?.focus();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div data-ds="" data-testid="ds-topbar" className={cx(dsFont, "flex flex-wrap items-center justify-end gap-2.5")}>
      <label className="ml-auto flex h-[38px] min-w-0 max-w-[340px] flex-[1_1_220px] items-center gap-2.5 rounded-ds-pill bg-ds-glass px-3.5 text-ds-ink-3">
        <Search className="size-[15px] shrink-0" aria-hidden />
        <input
          id="ds-topbar-search"
          data-testid="ds-search"
          type="search"
          value={searchValue}
          placeholder={searchPlaceholder}
          aria-label={t("ds.topbar.searchLabel")}
          className="m-0 min-w-0 flex-1 border-0 bg-transparent p-0 font-ds text-ds-body font-normal text-ds-ink outline-none placeholder:text-ds-ink-3"
          onChange={(event) => onSearchChange(event.target.value)}
        />
        <kbd className="font-ds text-ds-badge font-normal text-ds-ink-3">/</kbd>
      </label>
      <Button variant="icon" aria-label={t("ds.topbar.notifications")} data-testid="ds-notify" onClick={onNotify}>
        <Bell className="size-[18px]" aria-hidden />
      </Button>
      <Button variant="primary" data-testid="ds-primary" onClick={onPrimary}>
        {primaryLabel}
      </Button>
    </div>
  );
}
